import { useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { cbamReportingPeriodConfig } from '@/config/cbamReportingPeriods'
import { useGetAllCbamReports } from '@/features/cbam-reporting/hooks/cbamReports'
import { useGetAllImportedGoods } from '@/features/cbam-reporting/hooks/importedGoods'
import { useImportedGoodsWithLiability } from '@/features/cbam-reporting/hooks/useImportedGoodsWithLiability'
import type { ImportedGoodExtended } from '@/features/cbam-reporting/hooks/useImportedGoodsFilters'
import { useGetAllSupplierEmissionsReportsForOrganization } from '@/features/suppliers/hooks/supplierEmissionsReport'
import {
  buildLiabilityOverviewModel,
  LiabilityOverviewTemplate
} from '@/shared/components/cbam-good-details/liability-overview'
import { LoadingScreen } from '@/shared/components'
import { useGoodsCodes } from '@/shared/hooks/goodsCodes'
import useI18n from '@/shared/hooks/usei18n'
import { useSession } from '@/shared/hooks/useSession'
import { getDefaultCbamModifiers } from '@/shared/types/cbamModifiers'

/** Matches reporting details: estimator batch from 2026 onwards. */
const CBAM_REPORTING_ESTIMATOR_LAYOUT_FROM_YEAR = 2026

export default function ImportedGoodLiabilityOverviewPdf() {
  const { importedGoodId } = useParams<{ importedGoodId: string }>()
  const { search } = useLocation()
  const { t, handleChangeLanguage } = useI18n()
  const lang = new URLSearchParams(search).get('lang')

  const { session } = useSession()
  const organizationId = session.organization.id

  const importedGoodsQuery = useGetAllImportedGoods(organizationId)
  const cbamReportsQuery = useGetAllCbamReports(organizationId)
  const supplierEmissionsReportsQuery =
    useGetAllSupplierEmissionsReportsForOrganization(organizationId)
  const { data: goodsCodes } = useGoodsCodes({ aggregatedGoodsCategories: undefined })

  useEffect(() => {
    document.body.style.setProperty('background-color', 'white')
  }, [])

  useEffect(() => {
    if (lang) {
      handleChangeLanguage(lang)
    }
  }, [lang, handleChangeLanguage])

  const importedGood = useMemo(() => {
    if (!importedGoodId || !importedGoodsQuery.data) {
      return null
    }
    return importedGoodsQuery.data.find(g => g.id === importedGoodId && g.deletedAt == null) ?? null
  }, [importedGoodId, importedGoodsQuery.data])

  const cbamReport = useMemo(() => {
    if (!importedGood?.cbamReportId || !cbamReportsQuery.data) {
      return null
    }
    return cbamReportsQuery.data.find(r => r.id === importedGood.cbamReportId) ?? null
  }, [importedGood?.cbamReportId, cbamReportsQuery.data])

  const reportingPeriodYear = useMemo(() => {
    if (!cbamReport?.periodId) {
      return undefined
    }
    return cbamReportingPeriodConfig[cbamReport.periodId]?.year
  }, [cbamReport?.periodId])

  const supplierEmissionsReports = supplierEmissionsReportsQuery.data ?? []

  const importedGoodExtended = useMemo((): ImportedGoodExtended | null => {
    if (!importedGood) {
      return null
    }
    return {
      ...importedGood,
      emissionsReport:
        supplierEmissionsReports.find(r => r.id === importedGood.supplierEmissionsReportId) ??
        null,
      goodsReceivedReportFileName: null
    }
  }, [importedGood, supplierEmissionsReports])

  const importedGoodsWithLiabilityOptions = useMemo(() => {
    if (
      reportingPeriodYear == null ||
      reportingPeriodYear < CBAM_REPORTING_ESTIMATOR_LAYOUT_FROM_YEAR
    ) {
      return { useEstimatorBatchLiability: false as const }
    }
    return {
      useEstimatorBatchLiability: true as const,
      modifiers: cbamReport?.modifiers ?? getDefaultCbamModifiers()
    }
  }, [reportingPeriodYear, cbamReport?.modifiers])

  const { liabilityData, isBatchLiabilityLoading } = useImportedGoodsWithLiability(
    importedGoodExtended ? [importedGoodExtended] : [],
    supplierEmissionsReports,
    importedGoodsWithLiabilityOptions
  )

  const liabilityRow = useMemo(() => {
    if (!importedGood || !liabilityData?.results) {
      return null
    }
    return liabilityData.results.find(r => r.id === importedGood.id) ?? null
  }, [importedGood, liabilityData?.results])

  const modifiers = cbamReport?.modifiers ?? getDefaultCbamModifiers()

  const aggregatedGoodsCategory = useMemo(() => {
    if (!importedGood) {
      return undefined
    }
    return goodsCodes?.find(gc => gc.goodsCode === importedGood.cnCode)
      ?.aggregatedGoodsCategory
  }, [importedGood, goodsCodes])

  const model = useMemo(() => {
    if (!importedGood || !liabilityRow || reportingPeriodYear == null) {
      return null
    }
    const supplierEmissionsReport = importedGoodExtended?.emissionsReport ?? null
    return buildLiabilityOverviewModel({
      liabilityData: liabilityRow,
      good: {
        quarter: importedGood.quarter,
        cnCode: importedGood.cnCode,
        description: importedGood.description,
        netMassInTonnes: importedGood.netMassInTonnes,
        isUsingDefaultEmissions: importedGood.isUsingDefaultEmissions,
        country: importedGood.country
      },
      supplierEmissionsReport,
      modifiers,
      projectionYear: reportingPeriodYear,
      aggregatedGoodsCategory,
      t
    })
  }, [
    importedGood,
    liabilityRow,
    reportingPeriodYear,
    importedGoodExtended?.emissionsReport,
    modifiers,
    aggregatedGoodsCategory,
    t
  ])

  const isInitialLoading =
    importedGoodsQuery.isLoading ||
    cbamReportsQuery.isLoading ||
    supplierEmissionsReportsQuery.isLoading

  if (isInitialLoading) {
    return <LoadingScreen />
  }

  if (importedGoodExtended && isBatchLiabilityLoading) {
    return <LoadingScreen />
  }

  return (
    <div className="min-h-screen bg-white page-padding print:mt-0">
      <LiabilityOverviewTemplate model={model} originCountryCode={importedGood?.country ?? ''} />
      {/* do not remove the below line as it is needed for PDF */}
      <div data-artem="render-completed" />
    </div>
  )
}
